import { FC, useEffect } from "react";
import { Controller } from "react-hook-form";
import useI18n from "../../../hooks/useTranslations";
import { BorderConfigurationData } from "../../../http/border/types/configurations";
import Button from "../../ui/Button";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import LoadingSpinner from "../../ui/LoadingSpinner";
import Numeric from "../../ui/Numeric";
import { millimetersToMeters } from "./BorderManualMeasurementsStep";
import styles from "./BorderAggregateMeasurementsStep.module.css";

export type BorderAggregateMeasurementsFormBody = {
  perimeterLength: number | null;
  perimeterLengthParallel: number | null;
  perimeterLengthPerpendicular: number | null;
  cornerCountTotal: number | null;
  cornerCountInternal: number | null;
  cornerCountExternal: number | null;
};

const toFormBody = (borderData: BorderConfigurationData): BorderAggregateMeasurementsFormBody => ({
  perimeterLength: millimetersToMeters(borderData.perimeterLength ?? null),
  perimeterLengthParallel: millimetersToMeters(borderData.perimeterLengthParallel ?? null),
  perimeterLengthPerpendicular: millimetersToMeters(borderData.perimeterLengthPerpendicular ?? null),
  cornerCountTotal: borderData.cornerCountTotal ?? null,
  cornerCountInternal: borderData.cornerCountInternal ?? null,
  cornerCountExternal: borderData.cornerCountExternal ?? null,
});

type BorderAggregateMeasurementsStepProps = {
  borderData: BorderConfigurationData;
  isPerimeterSplitRequired: boolean;
  isCornerOrientationRequired: boolean;
  canEdit?: boolean;
  isAdmin?: boolean;
  isLoading?: boolean;
  next: () => void;
  back: () => void;
  onSubmit: (body: BorderAggregateMeasurementsFormBody) => Promise<void> | undefined;
};

const BorderAggregateMeasurementsStep: FC<BorderAggregateMeasurementsStepProps> = ({
  borderData,
  isPerimeterSplitRequired,
  isCornerOrientationRequired,
  canEdit,
  isAdmin,
  isLoading,
  next,
  back,
  onSubmit,
}) => {
  const i18n = useI18n();
  const defaultValues = toFormBody(borderData);
  const showCornerOrientationFields = isCornerOrientationRequired && borderData.span < 1500;

  return (
    <Form<BorderAggregateMeasurementsFormBody>
      mode="onBlur"
      defaultValues={defaultValues}
      disabled={isLoading || !canEdit}
      onSubmit={() => async (formData) =>
        isAdmin ? await onSubmit(formData) : canEdit ? await onSubmit(formData)?.then(() => next()) : next()
      }
      submitText={isAdmin ? i18n.translation.common.save : i18n.translation.common.next}
      secondaryButton={
        <>
          {isLoading && <LoadingSpinner delayed={0} size="small" />}
          <Button
            buttonProps={{
              disabled: isLoading,
              onClick: back,
            }}
            type="secondary"
          >
            {i18n.translation.common.back}
          </Button>
        </>
      }
    >
      {({ control, setValue, reset }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [borderData]);

        const disabled = isLoading || !canEdit;

        return (
          <div className={styles.form}>
            {isPerimeterSplitRequired ? (
              <div className={styles.fieldGroup}>
                <Controller
                  name="perimeterLengthParallel"
                  control={control}
                  rules={{ required: true, validate: (value) => (value ?? 0) > 0 }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.common.perimeterLengthParallel} error={fieldState.error}>
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          {...field}
                          id={labelId}
                          isInvalid={isInvalid}
                          decimalScale={2}
                          allowNegative={false}
                          suffix=" m"
                          disabled={disabled}
                          onBlur={(value) =>
                            setValue("perimeterLengthParallel", value, { shouldValidate: true })
                          }
                        />
                      )}
                    </FormField>
                  )}
                />

                <Controller
                  name="perimeterLengthPerpendicular"
                  control={control}
                  rules={{ required: true, validate: (value) => (value ?? 0) > 0 }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.common.perimeterLengthPerpendicular} error={fieldState.error}>
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          {...field}
                          id={labelId}
                          isInvalid={isInvalid}
                          decimalScale={2}
                          allowNegative={false}
                          suffix=" m"
                          disabled={disabled}
                          onBlur={(value) =>
                            setValue("perimeterLengthPerpendicular", value, { shouldValidate: true })
                          }
                        />
                      )}
                    </FormField>
                  )}
                />
              </div>
            ) : (
              <Controller
                name="perimeterLength"
                control={control}
                rules={{ required: true, validate: (value) => (value ?? 0) > 0 }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.perimeterLength} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        {...field}
                        id={labelId}
                        isInvalid={isInvalid}
                        decimalScale={2}
                        allowNegative={false}
                        suffix=" m"
                        disabled={disabled}
                        onBlur={(value) => setValue("perimeterLength", value, { shouldValidate: true })}
                      />
                    )}
                  </FormField>
                )}
              />
            )}

            {showCornerOrientationFields ? (
              <div className={styles.fieldGroup}>
                <Controller
                  name="cornerCountInternal"
                  control={control}
                  rules={{ required: true, validate: (value) => (value ?? 0) >= 0 }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.common.cornerCountInternal} error={fieldState.error}>
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          {...field}
                          id={labelId}
                          isInvalid={isInvalid}
                          decimalScale={0}
                          allowNegative={false}
                          disabled={disabled}
                          onBlur={(value) => setValue("cornerCountInternal", value, { shouldValidate: true })}
                        />
                      )}
                    </FormField>
                  )}
                />

                <Controller
                  name="cornerCountExternal"
                  control={control}
                  rules={{ required: true, validate: (value) => (value ?? 0) >= 0 }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.common.cornerCountExternal} error={fieldState.error}>
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          {...field}
                          id={labelId}
                          isInvalid={isInvalid}
                          decimalScale={0}
                          allowNegative={false}
                          disabled={disabled}
                          onBlur={(value) => setValue("cornerCountExternal", value, { shouldValidate: true })}
                        />
                      )}
                    </FormField>
                  )}
                />
              </div>
            ) : (
              <Controller
                name="cornerCountTotal"
                control={control}
                rules={{ required: true, validate: (value) => (value ?? 0) >= 0 }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.cornerCountTotal} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        {...field}
                        id={labelId}
                        isInvalid={isInvalid}
                        decimalScale={0}
                        allowNegative={false}
                        disabled={disabled}
                        onBlur={(value) => setValue("cornerCountTotal", value, { shouldValidate: true })}
                      />
                    )}
                  </FormField>
                )}
              />
            )}
          </div>
        );
      }}
    </Form>
  );
};

export default BorderAggregateMeasurementsStep;
